/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Grid, Hidden, useMediaQuery, useTheme } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardRounded';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Text } from 'components';
import Widget from 'components/pages/Content/Widget';
import { ICustomTheme, WidgetsType } from 'interfaces';
import { TCurrentGlobalViewWidgetSet } from 'interfaces/widgets.types';

import {
  StyledIconButton,
  StyledIndicator,
  cssContainer,
  cssContainerGrouped,
  cssGrouped,
  cssLargeIcon,
  cssMessage,
} from './styles';

export const WidgetCarousel = ({
  currentGlobalViewWidgetSet,
  widgets,
  groupedWidgets,
  isFullWidth,
  actionPhase,
  isFromWidgetGlobalView,
}: {
  currentGlobalViewWidgetSet?: TCurrentGlobalViewWidgetSet;
  widgets?: WidgetsType[];
  groupedWidgets?: WidgetsType[][];
  isFullWidth: boolean;
  actionPhase?: string;
  isFromWidgetGlobalView: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  
  if (!widgets?.length && !groupedWidgets?.length) {
    return (
      <Text css={cssMessage}>
        <em>
          {(() => {
            switch (actionPhase) {
              case 'running':
                return t('widgets.no_widget_to_display_running');
              case 'incoming':
                return t('widgets.no_widget_to_display_incoming');
              case 'ended':
                return t('widgets.no_widget_to_display_ended');
              default:
                return t('widgets.no_widget_to_display');
            }
          })()}
        </em>
      </Text>
    );
  }

  if (widgets?.length === 1) {
    return (
      <div css={(t) => cssContainer(t as ICustomTheme, isMobile, isFromWidgetGlobalView)}>
        <Widget widget={widgets[0]} isFullWidth={isFullWidth} />
      </div>
    );
  }

  if (groupedWidgets?.length === 1) {
    return (
      <Grid
        container
        justify="center"
        css={(t) => cssContainerGrouped(t as ICustomTheme, isMobile, isFromWidgetGlobalView)}
      >
        {groupedWidgets[0].map((widget) => (
          <Grid item xs={4} key={widget['@id']}>
            <Widget widget={widget} third />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Carousel
      autoPlay
      stopOnHover
      infiniteLoop
      emulateTouch
      interval={3000}
      showThumbs={false}
      showStatus={false}
      css={(t) => cssContainer(t as ICustomTheme, isMobile, isFromWidgetGlobalView)}
      renderArrowPrev={(onClickHandler) => (
        <Hidden only="xs">
          <StyledIconButton left onClick={onClickHandler}>
            <ArrowBackIcon css={cssLargeIcon} />
          </StyledIconButton>
        </Hidden>
      )}
      renderArrowNext={(onClickHandler) => (
        <Hidden only="xs">
          <StyledIconButton right onClick={onClickHandler}>
            <ArrowForwardIcon css={cssLargeIcon} />
          </StyledIconButton>
        </Hidden>
      )}
      renderIndicator={(onClickHandler, isSelected) => (
        <StyledIndicator
          onClick={onClickHandler}
          isSelected={isSelected}
          role="button"
        />
      )}
    >
      {widgets?.length
        ? widgets.map((widget) => (
            <Widget
              key={widget['@id']}
              widget={widget}
              isFullWidth={isFullWidth}
              noMarginAdjust
              currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
            />
          ))
        : groupedWidgets?.map((groupedWidget) => (
            <Grid container justify="center" css={cssGrouped}>
              {groupedWidget.map((widget) => (
                <Grid item xs={4} key={widget['@id']}>
                  <Widget
                    widget={widget}
                    third
                    currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
                  />
                </Grid>
              ))}
            </Grid>
          ))}
    </Carousel>
  );
};

WidgetCarousel.defaultProps = {
  isFullWidth: false,
  isFromWidgetGlobalView: false,
};
